/**
 * GeometryImportDialog - Sprint X Phase X1
 * Modal for importing geometry from KML, KMZ, GeoJSON, GPX, CSV files.
 *
 * Mission-map unification (PR6): the dialog no longer saves anything. It previews (and optionally
 * simplifies) the file through /missions/{id}/import-geometry + /simplify-geometry of an existing
 * mission and hands exactly one usable flight area - one Polygon without holes or one LineString - to
 * `onGeometrySelected(geometry, source)`; the mission form then saves it with everything else.
 * Anything else (several features, points only, holes) is rejected with a message - choosing one of
 * several features is deferred (OI-077), never silently the first one. One upload limit for every
 * tariff (`maxFileBytes`, from GET /missions/flight-area/settings).
 */

import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Upload, FileUp, MapPin, AlertTriangle, CheckCircle,
  X, Loader2, Map, Sliders, ChevronDown
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../lib/api';
import { validateGeometryFile, detectFormat, formatBytes, MAX_GEOMETRY_FILE_BYTES } from '../lib/geometry-utils';
import { pickSingleFlightArea, toFlightAreaSource } from '../lib/flightArea';
import { getApiErrorMessage } from '../lib/apiErrors';

const GeometryImportDialog = ({
  isOpen,
  onClose,
  missionId,
  onGeometrySelected,
  maxFileBytes = MAX_GEOMETRY_FILE_BYTES
}) => {
  const { t, language, translate } = useLanguage();
  const tr = t.geometryImport || {};
  const fa = t.flightArea || {};

  const [file, setFile] = useState(null);
  const [formatHint, setFormatHint] = useState('auto');
  const [latCol, setLatCol] = useState('');
  const [lonCol, setLonCol] = useState('');
  const [gpxPrefer, setGpxPrefer] = useState('waypoints');

  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [simplifyTolerance, setSimplifyTolerance] = useState(0);
  const [simplifiedGeometry, setSimplifiedGeometry] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const f = acceptedFiles[0];
      const validation = validateGeometryFile(f, maxFileBytes);

      if (!validation.valid) {
        toast.error(validation.error === 'too_large'
          ? `${tr.fileTooLarge || 'File too large'} (max. ${formatBytes(maxFileBytes)})`
          : (tr.unsupportedFormat || 'Unsupported file type'));
        return;
      }

      setFile(f);
      setPreview(null);
      setSimplifiedGeometry(null);

      const format = detectFormat(f.name);
      if (format !== 'unknown') {
        setFormatHint(format);
      }
    }
  }, [maxFileBytes, tr.fileTooLarge, tr.unsupportedFormat]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      'application/vnd.google-earth.kml+xml': ['.kml'],
      'application/vnd.google-earth.kmz': ['.kmz'],
      'application/geo+json': ['.geojson'],
      'application/json': ['.json'],
      'application/gpx+xml': ['.gpx'],
      'text/csv': ['.csv']
    }
  });

  const handlePreview = async () => {
    if (!file) return;

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      let url = `/missions/${missionId}/import-geometry`;
      const params = new URLSearchParams();

      if (formatHint && formatHint !== 'auto') {
        params.append('format_hint', formatHint);
      }
      if (latCol) params.append('lat_col', latCol);
      if (lonCol) params.append('lon_col', lonCol);
      if (gpxPrefer) params.append('gpx_prefer', gpxPrefer);

      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await api.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Geometry uploads can be up to 50 MB (one limit for every tariff) — the shared api client's 30s
        // default (lib/api.js) is sized for ordinary JSON requests and would abort a large
        // upload+parse+validate round trip well before the server's own headroom is used.
        timeout: 180000
      });

      setPreview(response.data);
      toast.success(tr.previewLoaded || 'Preview loaded');

    } catch (error) {
      toast.error(getApiErrorMessage(error, language, tr.previewFailed || 'Failed to load preview'));
    } finally {
      setLoading(false);
    }
  };

  const handleSimplify = async (tolerance) => {
    if (!preview?.parsed_geometry) return;

    try {
      const response = await api.post(
        `/missions/${missionId}/simplify-geometry?tolerance_meters=${tolerance}`,
        { geometry: preview.parsed_geometry }
      );

      setSimplifiedGeometry(response.data);
    } catch (error) {
      console.error('Simplify error:', error);
    }
  };

  // The one flight area the form can take over - or why the file cannot be used yet.
  const selection = preview
    ? pickSingleFlightArea(simplifiedGeometry?.simplified_geometry || preview.parsed_geometry)
    : null;
  const selectionError = selection?.error ? {
    multiple: translate('flightArea.importSingleOnly', { count: selection.count }),
    points: fa.importPoints,
    holes: fa.importHoles,
    unsupported: fa.importUnsupported,
    empty: fa.importEmpty,
  }[selection.error] : null;

  const handleUse = () => {
    if (!selection?.geometry) return;
    onGeometrySelected?.(selection.geometry, toFlightAreaSource(preview?.source_format, file?.name));
    handleClose();
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setSimplifiedGeometry(null);
    setSimplifyTolerance(0);
    onClose();
  };

  const detectedFormat = file ? detectFormat(file.name) : null;
  const showCsvOptions = detectedFormat === 'csv' || formatHint === 'csv';
  const showGpxOptions = detectedFormat === 'gpx' || formatHint === 'gpx';

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="geometry-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            {tr.title || 'Import Geometry'}
          </DialogTitle>
          <DialogDescription>
            {tr.subtitle || 'Import flight zone from KML, GeoJSON, GPX, or CSV'}
          </DialogDescription>
        </DialogHeader>

        {/* Dropzone */}
        {!preview && (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
              ${isDragActive ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 hover:border-blue-400'}`}
            data-testid="geometry-dropzone"
          >
            <input {...getInputProps()} />
            <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            {isDragActive ? (
              <p className="text-blue-600">{tr.dropHere || 'Drop file here...'}</p>
            ) : (
              <>
                <p className="text-gray-600 dark:text-gray-300">
                  {tr.dragDrop || 'Drag & drop file here, or click to select'}
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  {tr.supportedFormats || 'Supported: KML, KMZ, GeoJSON, GPX, CSV'}
                </p>
              </>
            )}
          </div>
        )}

        {/* Selected file info */}
        {file && !preview && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileUp className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-gray-500">{formatBytes(file.size)}</p>
                </div>
              </div>
              <Badge>{detectedFormat?.toUpperCase()}</Badge>
            </div>

            {/* Format hint */}
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600 dark:text-gray-400">
                  {tr.formatHint || 'Format'}
                </label>
                <Select value={formatHint} onValueChange={setFormatHint}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">{tr.autoDetect || 'Auto-detect'}</SelectItem>
                    <SelectItem value="kml">KML</SelectItem>
                    <SelectItem value="kmz">KMZ</SelectItem>
                    <SelectItem value="geojson">GeoJSON</SelectItem>
                    <SelectItem value="gpx">GPX</SelectItem>
                    <SelectItem value="csv">CSV</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {showGpxOptions && (
                <div>
                  <label className="text-sm text-gray-600 dark:text-gray-400">
                    {tr.gpxPrefer || 'GPX Preference'}
                  </label>
                  <Select value={gpxPrefer} onValueChange={setGpxPrefer}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="waypoints">{tr.waypoints || 'Waypoints'}</SelectItem>
                      <SelectItem value="track">{tr.track || 'Track/Route'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {showCsvOptions && (
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600 dark:text-gray-400">
                    {tr.latColumn || 'Latitude Column'}
                  </label>
                  <Input
                    value={latCol}
                    onChange={e => setLatCol(e.target.value)}
                    placeholder="latitude"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 dark:text-gray-400">
                    {tr.lonColumn || 'Longitude Column'}
                  </label>
                  <Input
                    value={lonCol}
                    onChange={e => setLonCol(e.target.value)}
                    placeholder="longitude"
                  />
                </div>
              </div>
            )}

            <Button
              type="button"
              onClick={handlePreview}
              className="w-full mt-4"
              disabled={loading}
              data-testid="preview-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Map className="w-4 h-4 mr-2" />}
              {tr.loadPreview || 'Load Preview'}
            </Button>
          </div>
        )}

        {/* Preview results */}
        {preview && (
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-600">{preview.stats?.feature_count || 0}</p>
                <p className="text-sm text-gray-600">{tr.features || 'Features'}</p>
              </div>
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-600">{preview.stats?.total_points || 0}</p>
                <p className="text-sm text-gray-600">{tr.points || 'Points'}</p>
              </div>
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center">
                <p className="text-2xl font-bold text-purple-600">
                  {preview.stats?.total_area_km2?.toFixed(2) || '0.00'}
                </p>
                <p className="text-sm text-gray-600">km2</p>
              </div>
            </div>

            {/* Validation */}
            <div className="p-4 rounded-lg border">
              <div className="flex items-center gap-2 mb-2">
                {preview.validation?.is_valid ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                )}
                <span className="font-medium">
                  {preview.validation?.is_valid
                    ? (tr.validGeometry || 'Valid Geometry')
                    : (tr.invalidGeometry || 'Invalid Geometry')}
                </span>
              </div>

              {preview.validation?.warnings?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {preview.validation.warnings.map((w, i) => (
                    <p key={i} className="text-sm text-amber-600 flex items-start gap-1">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      {w}
                    </p>
                  ))}
                </div>
              )}

              {preview.validation?.errors?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {preview.validation.errors.map((e, i) => (
                    <p key={i} className="text-sm text-red-600 flex items-start gap-1">
                      <X className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      {e}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Simplify slider */}
            {preview.stats?.total_points > 50 && (
              <div className="p-4 rounded-lg border">
                <div className="flex items-center gap-2 mb-3">
                  <Sliders className="w-4 h-4" />
                  <span className="font-medium">{tr.simplify || 'Simplify Geometry'}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-500">0m</span>
                  <Slider
                    value={[simplifyTolerance]}
                    onValueChange={([v]) => {
                      setSimplifyTolerance(v);
                      if (v > 0) handleSimplify(v);
                    }}
                    max={500}
                    step={10}
                    className="flex-1"
                  />
                  <span className="text-sm text-gray-500">500m</span>
                </div>
                {simplifiedGeometry && (
                  <p className="text-sm text-gray-600 mt-2">
                    {tr.reduced || 'Reduced'}: {simplifiedGeometry.original_points} → {simplifiedGeometry.simplified_points}
                    {' '}({simplifiedGeometry.reduction_percent}%)
                  </p>
                )}
              </div>
            )}

            {selectionError && (
              <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 flex items-start gap-2" data-testid="import-selection-error">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {selectionError}
              </div>
            )}

            {/* Suggestions */}
            {preview.suggestions?.length > 0 && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <p className="text-sm font-medium text-amber-700 mb-1">{tr.suggestions || 'Suggestions'}:</p>
                <ul className="text-sm text-amber-600 list-disc list-inside">
                  {preview.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={handleClose}>
            {tr.cancel || 'Cancel'}
          </Button>
          {preview && (
            <Button
              type="button"
              onClick={handleUse}
              disabled={loading || !preview.validation?.is_valid || !selection?.geometry}
              data-testid="commit-geometry-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {fa.useGeometry || 'Use geometry'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GeometryImportDialog;
